// Library Methods
import { useTranslation } from "react-i18next";
import { useMemo } from "react";

// Components
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Typography from "@mui/material/Typography";
import { DownloadIcon } from "src/assets/icons";

// Constants
import { ReportFileType, ReportStatus } from "src/constants/enums";

// Types
import { FileInfoType } from "src/types/sharedTypes";

type DownloadButtonContentType = {
  fileData: FileInfoType;
  fileType: ReportFileType;
  reportInfoError: boolean;
  firstLoadingDone: boolean | null;
};

const DownloadButtonContent = ({
  fileData,
  fileType,
  reportInfoError,
  firstLoadingDone,
}: DownloadButtonContentType) => {
  const { t } = useTranslation();

  const isFailedReport = useMemo(
    () =>
      reportInfoError ||
      fileData?.hasBlobStorageError ||
      fileData?.reportInfoData?.status === ReportStatus.Failed,
    [
      reportInfoError,
      fileData?.hasBlobStorageError,
      fileData?.reportInfoData?.status,
    ]
  );

  const isUnavailableReport = useMemo(
    () =>
      fileData?.reportInfoData?.status === ReportStatus.Unavailable ||
      (firstLoadingDone && !fileData?.reportInfoData?.status),
    [firstLoadingDone, fileData?.reportInfoData?.status]
  );

  const isPendingReport = useMemo(
    () =>
      !firstLoadingDone ||
      fileData?.reportInfoData?.status === ReportStatus.Pending ||
      fileData?.reportInfoData?.status === ReportStatus.InProgress,
    [firstLoadingDone, fileData?.reportInfoData?.status]
  );

  if (isFailedReport) {
    return <Typography>{t("Failed")}</Typography>;
  }

  if (isUnavailableReport) {
    return <Typography>{t("Unavailable")}</Typography>;
  }

  if (isPendingReport) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          gap: "8px",
        }}
      >
        <Typography sx={{ textTransform: "none" }}>
          {t("Creating {{ fileName }}", {
            fileName: fileType,
          })}
        </Typography>
        <CircularProgress size={20} sx={{ color: "inherit" }} disableShrink />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        gap: "8px",
        width: "100%",
      }}
    >
      <DownloadIcon />
      <Typography>{fileType}</Typography>
      <CircularProgress
        size={20}
        sx={{ color: "inherit" }}
        disableShrink
        hidden={!fileData?.isDownloading}
      />
    </Box>
  );
};

export default DownloadButtonContent;
